import * as React from "react";
import { useEffect, useState } from "react";
import { useStyle } from "./style";
import { useTranslation } from "react-i18next";
import { RemoveIcon } from "@/components/icons/icons";
import { FormInput } from "@/components/form-inputs/form-input";
import { IInput } from "@/components/form-inputs/interfaces";
import { userInputs } from "../../inputs/user-inputs";
import { Button, Stack, Typography } from "@mui/material";
import { useSetRecoilState } from "recoil";
import { gomakeUserState } from "./gomakeUserState";
import { resetPassModalState } from "../../state";
import { useGomakeAxios } from "@/hooks/use-gomake-axios";
import { getAllRolesForDropdownApi } from "@/services/api-service/users/roles-api";
import { useDefaultLanguageOptions } from "@/hooks/use-default-language-options";
import ProductMultiSelect from "@/components/auto-complete/product-multi-select";
import { TrashIcon } from "@/components/icons/trash-icon";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";

interface IProps {
  user: {
    id?: string;
    username?: string;
    email?: string;
    userIPAddress?: string;
    isCanLoginWithCode?: boolean;
    roleId?: string;
    index?: number;
    productIds?: string[];
    isPendingInvitation?: boolean;
  };
  onDelete: (value: number) => void;
  setUser: any;
  productsList?: { id: string; name: string }[];
}

const UserForm = ({
  user,
  onDelete,
  setUser,
  productsList = [],
}: IProps) => {
  const setOpenModal = useSetRecoilState<boolean>(resetPassModalState);
  const setGomakeUser = useSetRecoilState<{}>(gomakeUserState);
  const { defaultLanguageOptions } = useDefaultLanguageOptions();
  const { callApi } = useGomakeAxios();
  const [rolesOptions, setRolesOptions] = useState<
    { value: string; text: string; isClientRole?: boolean }[]
  >([]);

  const { classes } = useStyle();
  const { t } = useTranslation();
  const { errorColor } = useGomakeTheme();
  const showPassFiled = !!(user.id && !user.isPendingInvitation);

  // Fetch roles once on mount
  useEffect(() => {
    const fetchRoles = async () => {
      const callBack = (res) => {
        if (res?.success) {
          const roles = res?.data?.map(
            (item: {
              value: string;
              text: string;
              isClientRole?: boolean;
            }) => ({
              value: item.value,
              text: item.text,
              isClientRole: item.isClientRole,
            })
          );
          setRolesOptions(roles);
        }
      };
      await getAllRolesForDropdownApi(callApi, callBack);
    };
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set default role when roles are loaded and user doesn't have a roleId
  useEffect(() => {
    if (rolesOptions.length > 0 && !user.roleId) {
      const defaultRole = rolesOptions.find(
        (role) => role.isClientRole === true
      );
      if (defaultRole) {
        setUser({
          ...user,
          roleId: defaultRole.value,
        });
      }
    }
  }, [rolesOptions, user, setUser]);

  const onChangeInputs = (key, value) => {
    if (key == "email") {
      setUser({ ...user, email: value, username: value });
    } else {
      setUser({ ...user, [key]: value });
    }
  };

  return (
    <div>
      {user.isPendingInvitation && (
        <Stack sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="primary">
            {t("customers.modal.invitationSent")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("customers.modal.invitationSentHint")}
          </Typography>
        </Stack>
      )}
      <div style={classes.customerInfoStyle}>
        {userInputs(user, defaultLanguageOptions, rolesOptions).map(
          (item) => (
            <FormInput
              key={item.parameterKey}
              input={item as IInput}
              changeState={onChangeInputs}
              error={item.required && !item.value}
              readonly={false}
            />
          )
        )}

        {productsList.length > 0 && (
          <Stack direction={"column"} gap={"10px"} width={"100%"} minWidth={"280px"} margin={"10px 0px"}>
            <span style={classes.inputLbl}>
              {t("customers.modal.allowedProducts")}
            </span>
            <ProductMultiSelect
              products={productsList}
              selectedProductIds={user?.productIds || []}
              onProductChange={(selectedIds) =>
                setUser({ ...user, productIds: selectedIds })
              }
              placeholder={t("customers.modal.selectProducts")}
              containerStyle={{ width: "100%" }}
              selectStyle={{ width: "40%", minWidth: "280px" }}
            />
          </Stack>
        )}

        {showPassFiled && (
          <Stack direction={"column"} gap={"10px"} width={"180px"}>
            <span style={classes.inputLbl}>
              {t("customers.modal.password")}
            </span>
            <Button
              onClick={() => {
                setGomakeUser(user);
                setOpenModal(true);
              }}
              variant={"contained"}
            >
              {t("customers.buttons.changePass")}
            </Button>
          </Stack>
        )}
      </div>

      <Stack direction={"row"} marginTop={"20px"} marginBottom={"15px"}>
        <a style={classes.removeFormStyle} onClick={() => onDelete(user.index)}>
          <TrashIcon color={errorColor(500)} height={20} width={20} />
          <button style={classes.buttonsStyle}>
            {t("customers.buttons.remove")}
          </button>
        </a>
      </Stack>
    </div>
  );
};

export { UserForm };
